import appConfig from '@/config/appConfig.typed';
import {externalLinkTypes} from '@/consts/externalLinkTypes';
import {ICC_INBOX_MESSAGE_STATUS_TYPES} from '@/consts/icc/iccInboxMessageStatusTypes';
import * as iccMethods from '@/consts/icc/iccMethods';
import iccMarketService from '@/services/icc/iccMarketService';
import server from '@/services/server/server';
import userBrowserService from '@/services/user/userBrowserService';
import {deleteInboxMessageAction, readInboxMessageAction, setInboxMessagesAction} from '@/state/slices/inboxSlice';
import stringUtils from '@/utils/stringUtils';
import uaUtils from '@/utils/uaUtils';
import urlUtils from '@/utils/urlUtils';

const FIREBASE_PLATFORM_TYPES = {
    WINDOWS: 'WEBFCM-WIN',
    MAC: 'WEBFCM-MAC',
    ANDROID: 'WEBFCM-AND',
    IOS: 'WEBFCM-IOS',
    DEFAULT: 'WEBFCM',
};

const getIccApiUrl = (methodName) => urlUtils.join(appConfig.getIccNotificationApiUrl(), methodName);

const getFirebasePlatformType = () => {
    if (uaUtils.isWindows()) {
        return FIREBASE_PLATFORM_TYPES.WINDOWS;
    } else if (uaUtils.isMac()) {
        return FIREBASE_PLATFORM_TYPES.MAC;
    } else if (uaUtils.isAndroid()) {
        return FIREBASE_PLATFORM_TYPES.ANDROID;
    } else if (userBrowserService.isIosDevice()) {
        return FIREBASE_PLATFORM_TYPES.IOS;
    } else {
        return FIREBASE_PLATFORM_TYPES.DEFAULT;
    }
};

const getFirebaseData = (subscription) => {
    return {
        platform: getFirebasePlatformType(),
        details: {
            fcm: {
                deviceToken: subscription.token,
            },
        },
    };
};

const pushSubscription = async (subscription) => {
    const data = getFirebaseData(subscription);

    const {
        data: {subscriptionId},
    } = await iccMarketService.callRequest({
        httpMethod: server.post,
        methodUrl: getIccApiUrl(iccMethods.SUBSCRIPTION_PUSH_DATA),
        requestConfig: {data},
        throwException: true,
    });

    return subscriptionId;
};

const deleteSubscription = async (subscriptionId) => {
    const result = await iccMarketService.callRequest({
        httpMethod: server.requestDelete,
        methodUrl: urlUtils.join(getIccApiUrl(iccMethods.SUBSCRIPTION_PUSH_DATA), subscriptionId),
        throwException: true,
    });

    return result;
};

const getInboxMessages = async ({limit = 10, nextPageStartKey}) => {
    const result = await iccMarketService.callRequest({
        httpMethod: server.get,
        methodUrl: getIccApiUrl(iccMethods.INBOX_MESSAGES_EXTENDED),
        requestConfig: {params: {limit, pageStartKey: nextPageStartKey}},
        action: setInboxMessagesAction,
        mapper: (data) => {
            data.messages = data.messages.map((item) => {
                if (item?.actionButton?.linkType) {
                    item.actionButton.linkType = externalLinkTypes.normalizeActionTypeCode(item.actionButton.linkType);
                }
                return item;
            });

            return data;
        },
        throwException: true,
    });

    return result;
};

const deleteInboxMessage = async (messageId) => {
    const result = await iccMarketService.callRequest({
        httpMethod: server.requestDelete,
        methodUrl: getIccApiUrl(iccMethods.INBOX_MESSAGES),
        requestConfig: {data: {messageIds: [messageId]}},
        action: () => deleteInboxMessageAction(messageId),
        throwException: true,
    });

    return result;
};

const updateInboxMessageStatus = async ({status = ICC_INBOX_MESSAGE_STATUS_TYPES.READ, messageId}) => {
    const result = await iccMarketService.callRequest({
        httpMethod: server.post,
        methodUrl: getIccApiUrl(stringUtils.formatByName(iccMethods.INBOX_UPDATE_MESSAGE_STATUS, {messageId})),
        requestConfig: {data: {status}},
        action: () => readInboxMessageAction(messageId),
        throwException: true,
    });

    return result;
};

export default {
    pushSubscription,
    deleteSubscription,
    getInboxMessages,
    deleteInboxMessage,
    updateInboxMessageStatus,
};
